/**
 * A small RFC 4180 reader.
 *
 * Broker exports are full of quoted fields containing the delimiter
 * ("Apple Inc., Class A") and occasionally embedded newlines, so a
 * `split(',')` loses rows silently. This tokenises properly and is the shared
 * front end for every statement parser.
 */

export interface ParseCsvOptions {
  /** Defaults to `,`. Lightyear and IBKR both use comma; some EU exports use `;`. */
  delimiter?: string;
}

export type CsvRow = string[];

export function parseCsv(input: string, options: ParseCsvOptions = {}): CsvRow[] {
  const delimiter = options.delimiter ?? ',';
  if (delimiter.length !== 1) throw new Error('CSV delimiter must be a single character');

  // Strip a UTF-8 BOM: Excel adds one and it corrupts the first header cell.
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;

  const rows: CsvRow[] = [];
  let row: CsvRow = [];
  let field = '';
  let inQuotes = false;
  let fieldWasQuoted = false;

  const endField = (): void => {
    row.push(fieldWasQuoted ? field : field.trim());
    field = '';
    fieldWasQuoted = false;
  };

  const endRow = (): void => {
    endField();
    // Drop rows that are entirely empty — trailing newlines are not data.
    if (row.length > 1 || row[0] !== '') rows.push(row);
    row = [];
  };

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;

    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"' && field === '') {
      inQuotes = true;
      fieldWasQuoted = true;
    } else if (char === delimiter) {
      endField();
    } else if (char === '\n') {
      endRow();
    } else if (char === '\r') {
      // Swallow CR; the following LF ends the row.
    } else {
      field += char;
    }
  }

  if (inQuotes) throw new CsvParseError('Unterminated quoted field at end of file');
  if (field !== '' || row.length > 0) endRow();

  return rows;
}

export class CsvParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvParseError';
  }
}

/** Map header names to column indexes, normalised so casing and spacing do not matter. */
export function headerIndex(header: CsvRow): Map<string, number> {
  const index = new Map<string, number>();
  header.forEach((name, position) => {
    index.set(normalizeHeader(name), position);
  });
  return index;
}

export function normalizeHeader(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}
